/**
 * Settings Page - Company and Invoice Settings
 */
import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { GlassPanel, Button, Input, LoadingSpinner, PageHeader, TabNav } from '../components/ui';
import { TAB_LINK_CLASSES } from '../components/ui/TabNav';
import { useSettings, useUpdateSettings } from '../hooks/useSettings';
import { useToast } from '../components/ui/Toast';
import {
  useOcrEngineState,
  useKiProviderState,
  useOcrButtonState,
} from '../hooks/useOcrEngineState';
import { OcrConfidenceBadge } from '../components/ocr/OcrConfidenceBadge';
import { useActiveTenantId } from '../hooks/useTenantContext';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';

export const Route = createFileRoute('/settings')({
  component: SettingsPage,
});

function SettingsPage() {
  const { data: settings, isLoading, error } = useSettings();
  const tenantId = useActiveTenantId();
  const updateSettings = useUpdateSettings();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<
    'company' | 'invoice' | 'reminder' | 'animation' | 'ocr'
  >('company');

  // Form state
  const [companyForm, setCompanyForm] = useState({
    name: '',
    street: '',
    postalCode: '',
    city: '',
    country: 'DE',
    tax_id: '',
    vat_id: '',
    bankName: '',
    iban: '',
    bic: '',
  });
  const companyDraft = useUnsavedChanges(companyForm, '/settings:company');
  const companyHydratedTenant = useRef<string | null>(null);
  const companyHydrationPending = useRef<string | null>(null);

  // Animation settings (localStorage-based)
  // T522: master enable flag — default ON. Mirrors the
  // `prefers-reduced-motion` OS preference for accessibility.
  const [animEnabled, setAnimEnabled] = useState(() => {
    const saved = localStorage.getItem('animation_enabled');
    if (saved === '0' || saved === 'false') return false;
    return true;
  });
  const [animSpeed, setAnimSpeed] = useState(() => {
    const saved = localStorage.getItem('animation_speed');
    return saved ? parseFloat(saved) : 0.002;
  });
  const [animParticleX, setAnimParticleX] = useState(() => {
    const saved = localStorage.getItem('animation_particle_x');
    return saved ? parseInt(saved) : 80;
  });
  const [animParticleZ, setAnimParticleZ] = useState(() => {
    const saved = localStorage.getItem('animation_particle_z');
    return saved ? parseInt(saved) : 40;
  });
  const [animWave, setAnimWave] = useState(() => {
    const saved = localStorage.getItem('animation_wave');
    return saved ? parseFloat(saved) : 2.5;
  });
  const [animNoise, setAnimNoise] = useState(() => {
    const saved = localStorage.getItem('animation_noise');
    return saved ? parseFloat(saved) : 1.2;
  });

  const [invoiceForm, setInvoiceForm] = useState({
    default_payment_days: 14,
    vat_rate: 19,
    number_format: 'YYYY-NNNN',
  });
  const invoiceDraft = useUnsavedChanges(invoiceForm, '/settings:invoice');
  const invoiceHydratedTenant = useRef<string | null>(null);
  const invoiceHydrationPending = useRef<string | null>(null);

  const companyFingerprint = JSON.stringify(companyForm);
  useEffect(() => {
    if (!settings || !tenantId) return;
    if (companyHydrationPending.current === companyFingerprint) {
      companyDraft.markSaved();
      companyHydratedTenant.current = tenantId;
      companyHydrationPending.current = null;
      return;
    }
    if (companyDraft.isDirty || companyHydratedTenant.current === tenantId) return;

    const next = {
      name: settings.company?.name || '',
      street: settings.company?.address?.street || '',
      postalCode: settings.company?.address?.postalCode || '',
      city: settings.company?.address?.city || '',
      country: settings.company?.address?.country || 'DE',
      tax_id: settings.company?.tax_id || '',
      vat_id: settings.company?.vat_id || '',
      bankName: settings.company?.bank_account?.bankName || '',
      iban: settings.company?.bank_account?.iban || '',
      bic: settings.company?.bank_account?.bic || '',
    };
    const fingerprint = JSON.stringify(next);
    if (fingerprint === companyFingerprint) {
      companyDraft.markSaved();
      companyHydratedTenant.current = tenantId;
      return;
    }
    companyHydrationPending.current = fingerprint;
    setCompanyForm(next);
  }, [settings, tenantId, companyFingerprint, companyDraft.isDirty, companyDraft.markSaved]);

  const invoiceFingerprint = JSON.stringify(invoiceForm);
  useEffect(() => {
    if (!settings || !tenantId) return;
    if (invoiceHydrationPending.current === invoiceFingerprint) {
      invoiceDraft.markSaved();
      invoiceHydratedTenant.current = tenantId;
      invoiceHydrationPending.current = null;
      return;
    }
    if (invoiceDraft.isDirty || invoiceHydratedTenant.current === tenantId) return;

    const next = {
      default_payment_days: settings.invoice?.default_payment_days ?? 14,
      vat_rate: settings.invoice?.vat_rate ?? 19,
      number_format: settings.invoice?.number_format ?? 'YYYY-NNNN',
    };
    const fingerprint = JSON.stringify(next);
    if (fingerprint === invoiceFingerprint) {
      invoiceDraft.markSaved();
      invoiceHydratedTenant.current = tenantId;
      return;
    }
    invoiceHydrationPending.current = fingerprint;
    setInvoiceForm(next);
  }, [settings, tenantId, invoiceFingerprint, invoiceDraft.isDirty, invoiceDraft.markSaved]);

  const handleSaveCompany = async () => {
    // A settings form may remain mounted while the active tenant changes.
    // Its hydrated-tenant ref is the ownership boundary for this draft.
    if (!tenantId || companyHydratedTenant.current !== tenantId) {
      addToast(
        'error',
        'Änderungen zuerst verwerfen und Einstellungen des aktiven Mandanten laden'
      );
      return;
    }
    try {
      await updateSettings.mutateAsync({
        company: {
          name: companyForm.name,
          address: {
            street: companyForm.street,
            postalCode: companyForm.postalCode,
            city: companyForm.city,
            country: companyForm.country,
          },
          tax_id: companyForm.tax_id,
          vat_id: companyForm.vat_id,
          bank_account: {
            bankName: companyForm.bankName,
            iban: companyForm.iban,
            bic: companyForm.bic,
          },
        },
      });
      companyDraft.markSaved();
      addToast('success', 'Firmendaten gespeichert');
    } catch {
      addToast('error', 'Fehler beim Speichern');
    }
  };

  const handleSaveInvoice = async () => {
    if (!tenantId || invoiceHydratedTenant.current !== tenantId) {
      addToast(
        'error',
        'Änderungen zuerst verwerfen und Einstellungen des aktiven Mandanten laden'
      );
      return;
    }
    try {
      await updateSettings.mutateAsync({
        invoice: invoiceForm,
      });
      invoiceDraft.markSaved();
      addToast('success', 'Rechnungseinstellungen gespeichert');
    } catch {
      addToast('error', 'Fehler beim Speichern');
    }
  };

  const companyTenantMismatch = Boolean(
    companyDraft.isDirty && (!tenantId || companyHydratedTenant.current !== tenantId)
  );
  const invoiceTenantMismatch = Boolean(
    invoiceDraft.isDirty && (!tenantId || invoiceHydratedTenant.current !== tenantId)
  );

  function discardCompanyDraftForActiveTenant() {
    if (!settings || !tenantId) return;
    const next = {
      name: settings.company?.name || '',
      street: settings.company?.address?.street || '',
      postalCode: settings.company?.address?.postalCode || '',
      city: settings.company?.address?.city || '',
      country: settings.company?.address?.country || 'DE',
      tax_id: settings.company?.tax_id || '',
      vat_id: settings.company?.vat_id || '',
      bankName: settings.company?.bank_account?.bankName || '',
      iban: settings.company?.bank_account?.iban || '',
      bic: settings.company?.bank_account?.bic || '',
    };
    companyHydrationPending.current = JSON.stringify(next);
    setCompanyForm(next);
    if (JSON.stringify(next) === companyFingerprint) {
      companyDraft.markSaved();
      companyHydratedTenant.current = tenantId;
      companyHydrationPending.current = null;
    }
  }

  function discardInvoiceDraftForActiveTenant() {
    if (!settings || !tenantId) return;
    const next = {
      default_payment_days: settings.invoice?.default_payment_days ?? 14,
      vat_rate: settings.invoice?.vat_rate ?? 19,
      number_format: settings.invoice?.number_format ?? 'YYYY-NNNN',
    };
    invoiceHydrationPending.current = JSON.stringify(next);
    setInvoiceForm(next);
    if (JSON.stringify(next) === invoiceFingerprint) {
      invoiceDraft.markSaved();
      invoiceHydratedTenant.current = tenantId;
      invoiceHydrationPending.current = null;
    }
  }

  if (isLoading) {
    return (
      <ProtectedRoute>
        <AppShell>
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        </AppShell>
      </ProtectedRoute>
    );
  }

  if (error) {
    return (
      <ProtectedRoute>
        <AppShell>
          <GlassPanel>
            <div className="text-center py-8">
              <p className="text-red-400">Fehler beim Laden der Einstellungen</p>
            </div>
          </GlassPanel>
        </AppShell>
      </ProtectedRoute>
    );
  }

  const tabs = [
    { id: 'company', label: 'Firmendaten' },
    { id: 'invoice', label: 'Rechnungen' },
    { id: 'reminder', label: 'Mahnungen' },
    { id: 'animation', label: 'Animation' },
    { id: 'ocr', label: 'OCR & KI' },
  ] as const;

  return (
    <ProtectedRoute>
      <AppShell>
        <PageHeader title="Einstellungen" subtitle="Systemkonfiguration und Firmendaten" />

        {/* 006b P6: Pills → shared TabNav (Underline = Navigations-Vokabular,
            Pills bleiben Filtern vorbehalten; PATTERNS.md §4). Kategorien
            bleibt Cross-Route-Link (Spec 004 T023 FR-G6-SETTINGS-NAV, 6. Tab). */}
        <TabNav
          aria-label="Einstellungs-Bereiche"
          active={activeTab}
          onChange={(id) => setActiveTab(id as typeof activeTab)}
          items={tabs.map((tab) => ({
            id: tab.id,
            label: tab.label,
            testid: `settings-tab-${tab.id}`,
          }))}
          trailing={
            <Link
              to="/settings/categories"
              className={TAB_LINK_CLASSES}
              data-testid="settings-tab-categories"
            >
              Kategorien
            </Link>
          }
        />

        {/* Company Settings */}
        {activeTab === 'company' && (
          <div className="space-y-6">
            <fieldset
              disabled={updateSettings.isPending}
              className="border-0 p-0 m-0 min-w-0 space-y-6"
            >
              <GlassPanel>
                <h2 className="text-lg font-medium text-white mb-6">Firmeninformationen</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="md:col-span-2">
                    <Input
                      label="Firmenname"
                      value={companyForm.name}
                      onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                      placeholder="Ihre Firma GmbH"
                    />
                  </div>
                  <Input
                    label="Straße und Hausnummer"
                    value={companyForm.street}
                    onChange={(e) => setCompanyForm({ ...companyForm, street: e.target.value })}
                    placeholder="Musterstraße 1"
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      label="PLZ"
                      value={companyForm.postalCode}
                      onChange={(e) =>
                        setCompanyForm({ ...companyForm, postalCode: e.target.value })
                      }
                      placeholder="12345"
                    />
                    <div className="col-span-2">
                      <Input
                        label="Stadt"
                        value={companyForm.city}
                        onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })}
                        placeholder="Berlin"
                      />
                    </div>
                  </div>
                </div>
              </GlassPanel>

              <GlassPanel>
                <h2 className="text-lg font-medium text-white mb-6">Steuerinformationen</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Input
                    label="Steuernummer"
                    value={companyForm.tax_id}
                    onChange={(e) => setCompanyForm({ ...companyForm, tax_id: e.target.value })}
                    placeholder="12/345/67890"
                  />
                  <Input
                    label="USt-IdNr."
                    value={companyForm.vat_id}
                    onChange={(e) => setCompanyForm({ ...companyForm, vat_id: e.target.value })}
                    placeholder="DE123456789"
                  />
                </div>
              </GlassPanel>

              <GlassPanel>
                <h2 className="text-lg font-medium text-white mb-6">Bankverbindung</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="md:col-span-2">
                    <Input
                      label="Bank"
                      value={companyForm.bankName}
                      onChange={(e) => setCompanyForm({ ...companyForm, bankName: e.target.value })}
                      placeholder="Deutsche Bank"
                    />
                  </div>
                  <Input
                    label="IBAN"
                    value={companyForm.iban}
                    onChange={(e) => setCompanyForm({ ...companyForm, iban: e.target.value })}
                    placeholder="DE89 3704 0044 0532 0130 00"
                  />
                  <Input
                    label="BIC"
                    value={companyForm.bic}
                    onChange={(e) => setCompanyForm({ ...companyForm, bic: e.target.value })}
                    placeholder="COBADEFFXXX"
                  />
                </div>
              </GlassPanel>
            </fieldset>
            <div className="flex justify-end">
              <div className="flex items-center gap-3">
                {companyTenantMismatch && (
                  <>
                    <span role="alert" className="text-sm text-amber-200">
                      Mandant gewechselt. Änderungen erst verwerfen.
                    </span>
                    <Button
                      variant="secondary"
                      onClick={discardCompanyDraftForActiveTenant}
                      disabled={updateSettings.isPending}
                    >
                      Änderungen verwerfen
                    </Button>
                  </>
                )}
                <Button
                  onClick={handleSaveCompany}
                  loading={updateSettings.isPending}
                  disabled={companyTenantMismatch || updateSettings.isPending}
                >
                  Firmendaten speichern
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Invoice Settings */}
        {activeTab === 'invoice' && (
          <div className="space-y-6">
            <fieldset
              disabled={updateSettings.isPending}
              className="border-0 p-0 m-0 min-w-0 space-y-6"
            >
              <GlassPanel>
                <h2 className="text-lg font-medium text-white mb-6">Rechnungseinstellungen</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Input
                    label="Standard-Zahlungsziel (Tage)"
                    type="number"
                    min={0}
                    value={invoiceForm.default_payment_days}
                    onChange={(e) => {
                      const days = Number.parseInt(e.target.value, 10);
                      setInvoiceForm({
                        ...invoiceForm,
                        default_payment_days: Number.isNaN(days) ? 14 : days,
                      });
                    }}
                  />
                  <Input
                    label="Standard-MwSt. (%)"
                    type="number"
                    value={invoiceForm.vat_rate}
                    onChange={(e) => {
                      const rate = Number.parseInt(e.target.value, 10);
                      setInvoiceForm({ ...invoiceForm, vat_rate: Number.isNaN(rate) ? 19 : rate });
                    }}
                  />
                  <div className="md:col-span-2">
                    <Input
                      label="Rechnungsnummer-Format"
                      value={invoiceForm.number_format}
                      onChange={(e) =>
                        setInvoiceForm({ ...invoiceForm, number_format: e.target.value })
                      }
                      hint="YYYY = Jahr, NNNN = fortlaufende Nummer"
                    />
                  </div>
                </div>
              </GlassPanel>
            </fieldset>
            <div className="flex justify-end">
              <div className="flex items-center gap-3">
                {invoiceTenantMismatch && (
                  <>
                    <span role="alert" className="text-sm text-amber-200">
                      Mandant gewechselt. Änderungen erst verwerfen.
                    </span>
                    <Button
                      variant="secondary"
                      onClick={discardInvoiceDraftForActiveTenant}
                      disabled={updateSettings.isPending}
                    >
                      Änderungen verwerfen
                    </Button>
                  </>
                )}
                <Button
                  onClick={handleSaveInvoice}
                  loading={updateSettings.isPending}
                  disabled={invoiceTenantMismatch || updateSettings.isPending}
                >
                  Rechnungseinstellungen speichern
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Animation Settings */}
        {activeTab === 'animation' && (
          <div className="space-y-6">
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-6">Hintergrund-Animation</h2>
              <p className="text-white/50 mb-6 text-sm">
                Passe die Parameter der Partikel-Animation an
              </p>

              {/* T522: master enable/disable toggle. OS-level
                  `prefers-reduced-motion` always wins; this is the
                  in-app override for when the OS preference is unset. */}
              <div className="flex items-center justify-between gap-4 pb-6 mb-6 border-b border-white/[0.06]">
                <div>
                  <label htmlFor="anim-enabled" className="block text-sm font-medium text-white">
                    Animation aktiv
                  </label>
                  <p className="text-xs text-white/50 mt-1">
                    Aus = ruhiger Hintergrund. Spart GPU + reduziert Ablenkung. OS-Einstellung
                    "Bewegung reduzieren" überschreibt diese Wahl immer.
                  </p>
                </div>
                <input
                  id="anim-enabled"
                  type="checkbox"
                  checked={animEnabled}
                  onChange={(e) => setAnimEnabled(e.target.checked)}
                  data-testid="settings-animation-enabled"
                  className="w-5 h-5 rounded border-white/20 bg-white/5 text-sky-400 focus:ring-sky-400/30"
                />
              </div>

              <div className="space-y-8">
                <div>
                  <label className="block text-sm font-medium text-white mb-3">
                    Geschwindigkeit
                    <span className="ml-3 text-white/40 font-normal">{animSpeed.toFixed(4)}</span>
                  </label>
                  <input
                    type="range"
                    min="0.0001"
                    max="0.01"
                    step="0.0001"
                    value={animSpeed}
                    onChange={(e) => setAnimSpeed(parseFloat(e.target.value))}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white/90 [&::-webkit-slider-thumb]:cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-white/40 mt-1">
                    <span>Langsam</span>
                    <span>Schnell</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-3">
                    Partikel Breite (X-Achse)
                    <span className="ml-3 text-white/40 font-normal">{animParticleX}</span>
                  </label>
                  <input
                    type="range"
                    min="20"
                    max="150"
                    step="5"
                    value={animParticleX}
                    onChange={(e) => setAnimParticleX(parseInt(e.target.value))}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white/90 [&::-webkit-slider-thumb]:cursor-pointer"
                  />
                  <p className="text-xs text-white/40 mt-1">Mehr Partikel = höhere GPU-Last</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-3">
                    Partikel Tiefe (Z-Achse)
                    <span className="ml-3 text-white/40 font-normal">{animParticleZ}</span>
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="80"
                    step="5"
                    value={animParticleZ}
                    onChange={(e) => setAnimParticleZ(parseInt(e.target.value))}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white/90 [&::-webkit-slider-thumb]:cursor-pointer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-3">
                    Wellen-Amplitude
                    <span className="ml-3 text-white/40 font-normal">{animWave.toFixed(1)}</span>
                  </label>
                  <input
                    type="range"
                    min="0.5"
                    max="5"
                    step="0.1"
                    value={animWave}
                    onChange={(e) => setAnimWave(parseFloat(e.target.value))}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white/90 [&::-webkit-slider-thumb]:cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-white/40 mt-1">
                    <span>Flach</span>
                    <span>Wellig</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-3">
                    Rausch-Intensität (Bewegung)
                    <span className="ml-3 text-white/40 font-normal">{animNoise.toFixed(1)}</span>
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="3"
                    step="0.1"
                    value={animNoise}
                    onChange={(e) => setAnimNoise(parseFloat(e.target.value))}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white/90 [&::-webkit-slider-thumb]:cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-white/40 mt-1">
                    <span>Ruhig</span>
                    <span>Bewegt</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 mt-8 pt-6 border-t border-white/[0.06]">
                <Button
                  onClick={() => {
                    localStorage.setItem('animation_enabled', animEnabled ? '1' : '0');
                    localStorage.setItem('animation_speed', animSpeed.toString());
                    localStorage.setItem('animation_particle_x', animParticleX.toString());
                    localStorage.setItem('animation_particle_z', animParticleZ.toString());
                    localStorage.setItem('animation_wave', animWave.toString());
                    localStorage.setItem('animation_noise', animNoise.toString());
                    window.location.reload();
                  }}
                >
                  Speichern & Neu laden
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setAnimSpeed(0.002);
                    setAnimParticleX(80);
                    setAnimParticleZ(40);
                    setAnimWave(2.5);
                    setAnimNoise(1.2);
                    localStorage.removeItem('animation_speed');
                    localStorage.removeItem('animation_particle_x');
                    localStorage.removeItem('animation_particle_z');
                    localStorage.removeItem('animation_wave');
                    localStorage.removeItem('animation_noise');
                    window.location.reload();
                  }}
                >
                  Zurücksetzen
                </Button>
              </div>
            </GlassPanel>
          </div>
        )}

        {/* T478 — OCR & KI Engine State */}
        {activeTab === 'ocr' && <OcrSettingsTab />}

        {/* Reminder Settings */}
        {activeTab === 'reminder' && (
          <div className="space-y-6">
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-6">Mahnungseinstellungen</h2>
              <p className="text-white/50 mb-6">
                Konfigurieren Sie die automatischen Mahnungsstufen und Gebühren.
              </p>

              <div className="space-y-6">
                {/* Level 1 */}
                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06]">
                  <h3 className="font-medium text-white mb-4">1. Mahnung</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Tage nach Fälligkeit"
                      type="number"
                      value={settings?.reminder.level_1_days || 14}
                      disabled
                    />
                    <Input
                      label="Mahngebühr (€)"
                      type="number"
                      value={settings?.reminder.level_1_fee || 5}
                      disabled
                    />
                  </div>
                </div>

                {/* Level 2 */}
                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06]">
                  <h3 className="font-medium text-white mb-4">2. Mahnung</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Tage nach 1. Mahnung"
                      type="number"
                      value={settings?.reminder.level_2_days || 14}
                      disabled
                    />
                    <Input
                      label="Mahngebühr (€)"
                      type="number"
                      value={settings?.reminder.level_2_fee || 10}
                      disabled
                    />
                  </div>
                </div>

                {/* Level 3 */}
                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06]">
                  <h3 className="font-medium text-white mb-4">3. Mahnung</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Tage nach 2. Mahnung"
                      type="number"
                      value={settings?.reminder.level_3_days || 14}
                      disabled
                    />
                    <Input
                      label="Mahngebühr (€)"
                      type="number"
                      value={settings?.reminder.level_3_fee || 15}
                      disabled
                    />
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06]">
                  <Input
                    label="Verzugszinsen (% p.a.)"
                    type="number"
                    value={settings?.reminder.interest_rate || 5}
                    disabled
                    hint="Wird automatisch auf überfällige Beträge angewendet"
                  />
                </div>
              </div>

              <p className="text-white/40 text-sm mt-6">
                Mahnungseinstellungen können derzeit nicht geändert werden.
              </p>
            </GlassPanel>
          </div>
        )}
      </AppShell>
    </ProtectedRoute>
  );
}

/**
 * T478 — OCR + KI engine state surface (FR-013a × FR-020).
 *
 * Renders engine state + a disabled-visible-with-tooltip preview of the
 * OCR-dependent control buttons. Buttons are NOT hidden when disabled —
 * the user must see the feature surface.
 */
function OcrSettingsTab() {
  const ocr = useOcrEngineState();
  const ki = useKiProviderState();
  const buttonState = useOcrButtonState();
  return (
    <div className="space-y-6">
      <GlassPanel>
        <h2 className="text-lg font-medium text-white mb-4">OCR-Engine Status</h2>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-white/60 w-32">Tesseract.js:</span>
            <OcrConfidenceBadge
              kind={ocr === 'production' ? 'validated' : 'inactive'}
              label={
                ocr === 'production'
                  ? 'produktiv'
                  : ocr === 'mocked'
                    ? 'gemockt (Test-Modus)'
                    : 'deaktiviert'
              }
              testid={`settings-ocr-engine-${ocr}`}
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-white/60 w-32">KI-Provider:</span>
            <OcrConfidenceBadge
              kind={ki === 'configured' ? 'validated' : 'inactive'}
              label={
                ki === 'configured'
                  ? 'konfiguriert'
                  : ki === 'missing-token'
                    ? 'kein Token'
                    : 'deaktiviert'
              }
              testid={`settings-ki-provider-${ki}`}
            />
          </div>
        </div>
      </GlassPanel>

      <GlassPanel>
        <h2 className="text-lg font-medium text-white mb-4">OCR-abhängige Aktionen (Vorschau)</h2>
        <p className="text-white/50 mb-4 text-sm">
          Diese Buttons erscheinen auf den Belege-Detail- und Durcharbeite-Seiten. Bei nicht-aktiver
          Engine bleiben sie sichtbar (greyed-out), damit das Feature-Spektrum erkennbar ist — per
          FR-013a × FR-020.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button
            disabled={buttonState.disabled}
            title={buttonState.tooltip}
            data-testid="settings-ocr-rerun-preview"
          >
            OCR neu ausführen
          </Button>
          <Button
            disabled={buttonState.disabled}
            title={buttonState.tooltip}
            variant="secondary"
            data-testid="settings-ki-recognize-preview"
          >
            Mit KI erkennen
          </Button>
        </div>
        {buttonState.disabled && (
          <p className="text-amber-300/80 mt-4 text-sm" data-testid="settings-ocr-tooltip">
            {buttonState.tooltip}
          </p>
        )}
      </GlassPanel>
    </div>
  );
}
